"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LegacyTransaction = void 0;
const utils_1 = require("@ganache/utils");
const ethereum_address_1 = require("@ganache/ethereum-address");
const rlp_1 = require("@ganache/rlp");
const runtime_transaction_1 = require("./runtime-transaction");
const signing_1 = require("./signing");
class LegacyTransaction extends runtime_transaction_1.RuntimeTransaction {
    constructor(data, common, extra) {
        super(data, common, extra);
        this.type = utils_1.Quantity.from("0x0");
        if (Array.isArray(data)) {
            this.nonce = utils_1.Quantity.from(data[0]);
            this.gasPrice = this.effectiveGasPrice = utils_1.Quantity.from(data[1]);
            this.gas = utils_1.Quantity.from(data[2]);
            this.to = data[3].length == 0 ? null : ethereum_address_1.Address.from(data[3]);
            this.value = utils_1.Quantity.from(data[4]);
            this.data = utils_1.Data.from(data[5]);
            this.v = utils_1.Quantity.from(data[6]);
            this.r = utils_1.Quantity.from(data[7]);
            this.s = utils_1.Quantity.from(data[8]);
            this.raw = data;
            if (!extra) {
                // TODO(hack): Transactions that come from the database must not be
                // validated since they may come from a fork.
                const { from, serialized, hash } = this.computeIntrinsics(this.v, this.raw, this.common.chainId());
                this.from = from;
                this.serialized = serialized;
                this.hash = hash;
            }
        }
        else {
            this.gasPrice = this.effectiveGasPrice = utils_1.Quantity.from(data.gasPrice);
            this.validateAndSetSignature(data);
        }
    }
    maxGasPrice() {
        return this.gasPrice;
    }
    toJSON(common) {
        const json = {
            hash: this.hash,
            type: this.type,
            nonce: this.nonce,
            blockHash: this.blockHash ? this.blockHash : null,
            blockNumber: this.blockNumber ? this.blockNumber : null,
            transactionIndex: this.index ? this.index : null,
            from: this.from,
            to: this.to,
            value: this.value,
            gas: this.gas,
            gasPrice: this.gasPrice,
            input: this.data,
            v: this.v,
            r: this.r,
            s: this.s
        };
        if ((this.common || common).isActivatedEIP(2718)) {
            json.type = this.type;
        }
        return json;
    }
    static fromTxData(data, common, extra) {
        return new LegacyTransaction(data, common, extra);
    }
    static fromEIP2930AccessListTransaction(data, common) {
        if (Array.isArray(data)) {
            // remove 1st item, chainId, and 7th item, accessList
            return new LegacyTransaction(data.slice(1, 7).concat(data.slice(8)), common);
        }
        return new LegacyTransaction(data, common);
    }
    toVmTransaction() {
        const data = this.data.toBuffer();
        return {
            hash: () => utils_1.BUFFER_32_ZERO,
            common: this.common,
            nonce: this.nonce.toBigInt(),
            gasPrice: this.gasPrice.toBigInt(),
            gasLimit: this.gas.toBigInt(),
            to: this.to,
            value: this.value.toBigInt(),
            data,
            getSenderAddress: () => this.from,
            /**
             * the minimum amount of gas the tx must have (DataFee + TxFee + Creation Fee)
             */
            getBaseFee: () => {
                return this.calculateIntrinsicGas();
            },
            getUpfrontCost: () => {
                const { gas, gasPrice, value } = this;
                return gas.toBigInt() * gasPrice.toBigInt() + value.toBigInt();
            },
            supports: (capability) => {
                return false;
            }
        };
    }
    /**
     * sign a transaction with a given private key, then compute and set the `hash`.
     *
     * @param privateKey - Must be 32 bytes in length
     */
    signAndHash(privateKey) {
        if (this.v != null) {
            throw new Error("Internal Error: RuntimeTransaction `sign` called but transaction has already been signed");
        }
        // only legacy transactions can work with EIP-155 deactivated.
        // (EIP-2930 and EIP-1559 made EIP-155 obsolete and this logic isn't needed
        // for those transactions)
        const eip155IsActive = this.common.gteHardfork("spuriousDragon");
        let chainId;
        let raw;
        let data;
        let dataLength;
        let sig;
        if (eip155IsActive) {
            chainId = utils_1.Quantity.toBuffer(this.common.chainId());
            raw = this.toEthRawTransaction(chainId, utils_1.BUFFER_EMPTY, utils_1.BUFFER_EMPTY);
            data = (0, rlp_1.encodeRange)(raw, 0, 6);
            dataLength = data.length;
            const ending = (0, rlp_1.encodeRange)(raw, 6, 3);
            const msgHash = (0, utils_1.keccak)((0, rlp_1.digest)([data.output, ending.output], dataLength + ending.length));
            sig = (0, utils_1.ecsignLegacy)(msgHash, privateKey, this.common.chainId());
        }
        else {
            raw = this.toEthRawTransaction(utils_1.BUFFER_EMPTY, utils_1.BUFFER_EMPTY, utils_1.BUFFER_EMPTY);
            data = (0, rlp_1.encodeRange)(raw, 0, 6);
            dataLength = data.length;
            const msgHash = (0, utils_1.keccak)((0, rlp_1.digest)([data.output], dataLength));
            sig = (0, utils_1.ecsignLegacy)(msgHash, privateKey);
        }
        this.v = utils_1.Quantity.from(sig.v);
        this.r = utils_1.Quantity.from(sig.r);
        this.s = utils_1.Quantity.from(sig.s);
        raw[6] = this.v.toBuffer();
        raw[7] = this.r.toBuffer();
        raw[8] = this.s.toBuffer();
        this.raw = raw;
        const encodedSignature = (0, rlp_1.encodeRange)(raw, 6, 3);
        const ranges = [data.output, encodedSignature.output];
        const length = dataLength + encodedSignature.length;
        // serialized is the rlp encoded raw data
        this.serialized = (0, rlp_1.digest)(ranges, length);
        this.hash = utils_1.Data.from((0, utils_1.keccak)(this.serialized));
    }
    toEthRawTransaction(v, r, s) {
        return [
            this.nonce.toBuffer(),
            this.gasPrice.toBuffer(),
            this.gas.toBuffer(),
            this.to ? this.to.toBuffer() : utils_1.BUFFER_EMPTY,
            this.value.toBuffer(),
            this.data.toBuffer(),
            v,
            r,
            s
        ];
    }
    computeIntrinsics(v, raw, chainId) {
        return (0, signing_1.computeIntrinsicsLegacyTx)(v, raw, chainId);
    }
    updateEffectiveGasPrice() { }
}
exports.LegacyTransaction = LegacyTransaction;
//# sourceMappingURL=legacy-transaction.js.map